import { inject, Injectable, signal } from '@angular/core';
import { NewClientStateService } from './new-client-state.service';
import { OrderService } from '../order/order.service';
import {
  Address,
  HTTPError,
  HTTPResponse,
  IDefaultProdukt,
  INewClientConfirmationStatusResponse,
  IOlavEntry,
  IPersonalData,
  IProduktAuswahl,
  IProduktAuswahlParams,
  IProduktVerfuegbarkeit,
  ISolrOlavKeyAddress,
  NewClientDetailsDTO,
} from '@mwe/models';
import { ENVIRONMENT_SERVICE_TOKEN, FeatureToggleEnum, ProductCategoryEnum } from '@mwe/constants';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '../environment/environment.service';
import { lastValueFrom } from 'rxjs';
import { StaticContentService } from '../static-content/static-content.service';
import { ProfileService } from '../profile/profile.service';

@Injectable({
  providedIn: 'root',
})
export class NewClientService {
  http = inject(HttpClient);
  environmentService = inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN);
  private readonly orderService = inject(OrderService);
  private readonly profileService = inject(ProfileService);
  private readonly newClientStateService = inject(NewClientStateService);
  private readonly staticContentService = inject(StaticContentService);
  private readonly config = signal<any>(undefined);

  private async getConfig() {
    if (!this.config()) {
      const staticProducts = await this.staticContentService.loadProductsConfiguration();
      if (this.profileService.isFeatureToggleDisabled(FeatureToggleEnum.INTERNET_V2_ENABLED)) {
        staticProducts.tariffs.internet.privat = {
          name: 'SuperSchnell 500',
          shortcut: 'WEBTK24FIBER500',
          key: 'WEBTK24FIBER500',
        };
      }
      this.config.set(staticProducts);
    }
  }

  async getConfigPossibleCategories(): Promise<any> {
    await this.getConfig();
    return this.config().possibleCategories;
  }

  async getMultiProductCardCategories(): Promise<any> {
    await this.getConfig();
    return this.config().multiProductCardCategories;
  }

  async getMultiProductTariffRestrictions(): Promise<any> {
    await this.getConfig();
    return this.config().multiProductTariffRestrictions;
  }

  async getAvailability(selectedOlav: IOlavEntry): Promise<IProduktVerfuegbarkeit[]> {
    return this.orderService.getAvailability(selectedOlav.identAdresse ? selectedOlav.identAdresse : selectedOlav);
  }

  getInternetAvailability(gaa: string, tkConnId: string): Promise<IProduktVerfuegbarkeit[]> {
    return this.orderService.getFiberglassInternetAvailability(gaa, tkConnId);
  }

  async getProductsForSelection(params: IProduktAuswahlParams): Promise<HTTPResponse<IProduktAuswahl>> {
    return this.orderService.getProductsForSelection(params);
  }

  getAlreadySelectedProductCategories(): ProductCategoryEnum[] {
    return this.newClientStateService.orderDetails
      .filter(elem => elem.status === 'Anmeldung')
      .map(elem => ProductCategoryEnum[elem.category.toUpperCase()]);
  }

  getIpAddress(): Promise<{ remoteAddress: string }> {
    return lastValueFrom(this.http.get<{ remoteAddress: string }>('/api/public/newclient/ip-address'));
  }

  async getConfigDefaultTariffs(): Promise<IDefaultProdukt> {
    const customerType = this.environmentService.getPortalCustomerType();
    await this.getConfig();
    const tariffs = this.config()?.tariffs;
    return {
      strom: {
        tarifBeschreibung: tariffs?.strom[customerType].name,
        tarifKey: tariffs?.strom[customerType].shortcut,
        defaultOptions: tariffs?.strom[customerType].defaultOptions,
      },
      gas: {
        tarifBeschreibung: tariffs?.gas[customerType].name,
        tarifKey: tariffs?.gas[customerType].shortcut,
        defaultOptions: tariffs?.gas[customerType].defaultOptions,
      },
      internet: {
        tarifBeschreibung: tariffs?.internet[customerType].name,
        tarifKey: tariffs?.internet[customerType].shortcut,
      },
      iptvLight: this.config()?.possibleCategories.includes('Iptv')
        ? {
            tarifBeschreibung: tariffs?.iptvLight[customerType].name,
            tarifKey: tariffs?.iptvLight[customerType].shortcut,
          }
        : null,
      iptvPremium: this.config()?.possibleCategories.includes('Iptv')
        ? {
            tarifBeschreibung: tariffs?.iptvPremium[customerType].name,
            tarifKey: tariffs?.iptvPremium[customerType].shortcut,
          }
        : null,
      voip: this.config()?.possibleCategories.includes('Voip')
        ? {
            tarifBeschreibung: tariffs?.voip[customerType].name,
            tarifKey: tariffs?.voip[customerType].shortcut,
          }
        : null,
    };
  }

  async confirm(addressCode: string, newClientType: number, dataDTO: NewClientDetailsDTO): Promise<INewClientConfirmationStatusResponse> {
    try {
      return await lastValueFrom(
        this.http.post<INewClientConfirmationStatusResponse>(
          this.orderService.getResourceUrl('confirm') + '?addressCode=' + addressCode + '&newClientType=' + newClientType,
          dataDTO,
        ),
      );
    } catch (err) {
      throw new HTTPError(err.status, err.error, err.error?.message);
    }
  }

  async retry(submitId: number, newClientType: number, dataDTO: NewClientDetailsDTO): Promise<INewClientConfirmationStatusResponse> {
    try {
      return await lastValueFrom(
        this.http.put<INewClientConfirmationStatusResponse>(
          this.orderService.getResourceUrl('retry') + '?submitId=' + submitId + '&newClientType=' + newClientType,
          dataDTO,
        ),
      );
    } catch (err) {
      throw new HTTPError(err.status, err.error);
    }
  }

  confirmStatus(submitId: number, newClientType: number): Promise<INewClientConfirmationStatusResponse> {
    const url = this.orderService.getResourceUrl('confirmStatus') + '?submitId=' + submitId + '&newClientType=' + newClientType;
    return lastValueFrom(this.http.get<INewClientConfirmationStatusResponse>(url));
  }

  async getAddressFromOlavKey(olavKey: string, processType?: string): Promise<ISolrOlavKeyAddress> {
    return this.orderService.getAddressFromOlavKey(olavKey, processType);
  }

  async sendProductInterestRequest(personalData: IPersonalData, address: Address): Promise<string> {
    return lastValueFrom(
      this.http.post<string>(this.orderService.getResourceUrl('productInterest'), {
        personalData,
        address,
      }),
    );
  }
}
